import React from "react";

import { SectionCardSkeleton, SkeletonBlock } from "components/admin/loading";
import {
  SchoolPlayerProfileContactList,
  SchoolPlayerProfileFootprintBlock,
  SchoolPlayerProfileFootprintStack,
  SchoolPlayerProfileGuardianCard,
  SchoolPlayerProfileHeroBody,
  SchoolPlayerProfileHeroPanel,
  SchoolPlayerProfileMetadataGrid,
  SchoolPlayerProfilePerformancePrimaryGrid,
  SchoolPlayerProfilePerformanceSecondaryGrid,
  SchoolPlayerProfileTabStack,
  SchoolPlayerProfileTeamList,
  SchoolPlayerProfileTopGrid,
} from "pages/app/workspace/styles/school-player-profile.page.inline.styles";

export const SchoolPlayerOverviewTabSkeleton: React.FC = () => (
  <SchoolPlayerProfileTabStack aria-label="Loading player overview" role="status">
    <SchoolPlayerProfileTopGrid>
      <SectionCardSkeleton titleWidthClassName="w-36">
        <SchoolPlayerProfileHeroPanel>
          <SkeletonBlock className="mx-auto h-52 w-40 shrink-0 rounded-[22px] sm:mx-0 sm:min-h-[14rem] sm:w-40 md:w-44 lg:w-48" />
          <SchoolPlayerProfileHeroBody>
            <div className="min-w-0 space-y-3">
              <SkeletonBlock className="h-3 w-24 rounded-full" />
              <SkeletonBlock className="h-8 w-48 max-w-full rounded-[18px]" />
              <SkeletonBlock className="h-4 w-56 max-w-full rounded-full" />
              <div className="flex flex-wrap gap-2 pt-1">
                <SkeletonBlock className="h-7 w-20 rounded-full" />
                <SkeletonBlock className="h-7 w-16 rounded-full" />
              </div>
              <SkeletonBlock className="mt-2 h-16 w-full rounded-[18px]" />
            </div>

            <SchoolPlayerProfileMetadataGrid>
              {Array.from({ length: 3 }).map((_, index) => (
                <div className="brand-panel-muted rounded-[18px] px-4 py-3.5" key={index}>
                  <SkeletonBlock className="h-3 w-16 rounded-full" />
                  <SkeletonBlock className="mt-3 h-5 w-24 rounded-full" />
                </div>
              ))}
            </SchoolPlayerProfileMetadataGrid>
          </SchoolPlayerProfileHeroBody>
        </SchoolPlayerProfileHeroPanel>
      </SectionCardSkeleton>

      <SectionCardSkeleton titleWidthClassName="w-44">
        <SchoolPlayerProfileGuardianCard>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <SkeletonBlock className="h-6 w-40 rounded-full" />
            <SkeletonBlock className="h-7 w-20 rounded-full" />
          </div>
          <SchoolPlayerProfileContactList>
            {Array.from({ length: 3 }).map((_, index) => (
              <div className="brand-panel-muted rounded-[18px] px-4 py-3.5" key={index}>
                <SkeletonBlock className="h-3 w-14 rounded-full" />
                <SkeletonBlock className="mt-3 h-4 w-36 max-w-full rounded-full" />
              </div>
            ))}
          </SchoolPlayerProfileContactList>
        </SchoolPlayerProfileGuardianCard>
      </SectionCardSkeleton>
    </SchoolPlayerProfileTopGrid>

    <SectionCardSkeleton titleWidthClassName="w-40">
      <SchoolPlayerProfileFootprintStack>
        <SchoolPlayerProfileFootprintBlock>
          <SkeletonBlock className="h-3 w-16 rounded-full" />
          <div className="flex flex-wrap gap-2">
            <SkeletonBlock className="h-8 w-24 rounded-full" />
            <SkeletonBlock className="h-8 w-28 rounded-full" />
            <SkeletonBlock className="h-8 w-20 rounded-full" />
          </div>
        </SchoolPlayerProfileFootprintBlock>
        <SchoolPlayerProfileFootprintBlock>
          <SkeletonBlock className="h-3 w-24 rounded-full" />
          <SchoolPlayerProfileTeamList>
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                className="flex items-center gap-3 rounded-[18px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5"
                key={index}
              >
                <SkeletonBlock className="h-10 w-10 shrink-0 rounded-2xl" />
                <div className="min-w-0 flex-1 space-y-2">
                  <SkeletonBlock className="h-4 w-28 rounded-full" />
                  <SkeletonBlock className="h-3 w-20 rounded-full" />
                </div>
              </div>
            ))}
          </SchoolPlayerProfileTeamList>
        </SchoolPlayerProfileFootprintBlock>
      </SchoolPlayerProfileFootprintStack>
    </SectionCardSkeleton>
  </SchoolPlayerProfileTabStack>
);

export const SchoolPlayerPerformanceTabSkeleton: React.FC = () => (
  <SchoolPlayerProfileTabStack aria-label="Loading player performance" role="status">
    <SchoolPlayerProfilePerformancePrimaryGrid>
      <SectionCardSkeleton titleWidthClassName="w-32">
        <div className="space-y-3">
          <div className="rounded-[1.25rem] border border-[var(--brand-border)] bg-[var(--brand-hero-surface)] p-4">
            <SkeletonBlock className="h-3 w-24 rounded-full" />
            <SkeletonBlock className="mt-3 h-6 w-36 rounded-[18px]" />
            <SkeletonBlock className="mt-2 h-4 w-48 rounded-full" />
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div className="brand-panel-muted rounded-[18px] px-4 py-3.5" key={index}>
                  <SkeletonBlock className="h-3 w-14 rounded-full" />
                  <SkeletonBlock className="mt-3 h-6 w-12 rounded-full" />
                </div>
              ))}
            </div>
          </div>
          {Array.from({ length: 2 }).map((_, index) => (
            <div
              className="flex items-center justify-between gap-3 rounded-[18px] border border-[var(--brand-border)] px-3.5 py-3"
              key={index}
            >
              <div className="min-w-0 flex-1 space-y-2">
                <SkeletonBlock className="h-4 w-28 rounded-full" />
                <SkeletonBlock className="h-3 w-40 rounded-full" />
              </div>
              <SkeletonBlock className="h-3 w-24 rounded-full" />
            </div>
          ))}
        </div>
      </SectionCardSkeleton>

      <SectionCardSkeleton titleWidthClassName="w-28">
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              className="space-y-2 rounded-[18px] border border-[var(--brand-border)] px-3.5 py-3"
              key={index}
            >
              <div className="flex items-center justify-between gap-2">
                <SkeletonBlock className="h-4 w-24 rounded-full" />
                <SkeletonBlock className="h-3 w-28 rounded-full" />
              </div>
              <SkeletonBlock className="h-1.5 w-full rounded-full" />
            </div>
          ))}
        </div>
      </SectionCardSkeleton>
    </SchoolPlayerProfilePerformancePrimaryGrid>

    <SchoolPlayerProfilePerformanceSecondaryGrid>
      <SectionCardSkeleton titleWidthClassName="w-24">
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div className="flex gap-3 rounded-[18px] border border-[var(--brand-border)] px-3.5 py-3" key={index}>
              <SkeletonBlock className="h-4 w-10 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <SkeletonBlock className="h-4 w-36 rounded-full" />
                <SkeletonBlock className="h-3 w-44 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </SectionCardSkeleton>
      <SectionCardSkeleton titleWidthClassName="w-32">
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div className="rounded-[18px] border border-[var(--brand-border)] px-3.5 py-3" key={index}>
              <SkeletonBlock className="h-4 w-40 rounded-full" />
              <SkeletonBlock className="mt-2 h-3 w-48 rounded-full" />
            </div>
          ))}
        </div>
      </SectionCardSkeleton>
    </SchoolPlayerProfilePerformanceSecondaryGrid>
  </SchoolPlayerProfileTabStack>
);

export const SchoolPlayerProfileTabSkeleton: React.FC = () => (
  <SectionCardSkeleton subtitleWidthClassName="w-56" titleWidthClassName="w-40">
    <SkeletonBlock className="h-56 w-full rounded-[24px]" />
  </SectionCardSkeleton>
);
